import type { CustomCellRendererProps } from "ag-grid-react";
import { format } from "date-fns";
import React from "react";

import { faEllipsis } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCircleExclamation, faLock } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonStandard, DropDownMenu, Icon, Link, ProgressBar, Text, Tooltip } from "@performyard/py-components";
import { ReviewDetailsFrontend } from "@performyard/service-contracts";

import { appendCurrentLocationAsFrom } from "../../../../utils/reviewsBreadcrumbs";

export const AdminsOnlyCellRenderer = (
	props: CustomCellRendererProps<ReviewDetailsFrontend, ReviewDetailsFrontend["adminsOnly"]>
) => {
	return (
		<div className="flex h-full items-center justify-center">
			<Tooltip content="This review is locked to admins only">
				{props.value ? <Icon icon={faLock} size={16} color="neutral-7" /> : null}
			</Tooltip>
		</div>
	);
};

export const ActionCellRenderer = (
	props: CustomCellRendererProps<ReviewDetailsFrontend, ReviewDetailsFrontend["_id"]>
) => {
	if (!props?.data?._id) {
		return null;
	}

	return (
		<div className="flex h-full items-center">
			<Link href={appendCurrentLocationAsFrom(`/view-review/${props.data._id}`)}>
				<ButtonStandard size="small">Open Review</ButtonStandard>
			</Link>
		</div>
	);
};

export const SubjectGroupCellRenderer = (
	props: CustomCellRendererProps<ReviewDetailsFrontend, ReviewDetailsFrontend["subjectGroup"]>
) => {
	// Use the row data instead of the value because the value getter is overridden for CSV export
	const subjectGroup = props?.data?.subjectGroup;
	if (!subjectGroup?._id) {
		return null;
	}

	return (
		<div className="flex h-full items-center">
			<Link href={`/group/employees/${subjectGroup._id}`}>
				<Text size="paragraph-3" wrap={false} dangerouslySetClassName="truncate">
					{subjectGroup.name}
				</Text>
			</Link>
		</div>
	);
};

export const TotalProgressCellRenderer = (
	props: CustomCellRendererProps<ReviewDetailsFrontend, ReviewDetailsFrontend["totalProgressPercent"]>
) => {
	// Should be pre-formatted to percent in the value getter
	const value = props.value;

	if (value === null || value === undefined) {
		return null;
	}

	// If the review is completed, render the completion date and status
	if (props.data?.completed) {
		const formattedDate = props.data.completeDate ? format(props.data.completeDate, "MMM d, yyyy h:mmaaa") : null;

		const statusString = props.data.closed ? "Closed" : "Completed";

		if (props.data.completeDate) {
			// Render status and date
			// TODO: Use formatted date component from py-components when available
			return (
				<div className="flex h-full items-center">
					<Text size="paragraph-3" wrap={false} dangerouslySetClassName="truncate">
						{statusString} {formattedDate}
					</Text>
				</div>
			);
		}

		// Render status only
		return (
			<div className="flex h-full items-center">
				<Text size="paragraph-3" wrap={false} dangerouslySetClassName="truncate">
					{statusString}
				</Text>
			</div>
		);
	}

	// If the review is not completed, render the progress bar and percentage
	return (
		<div className="flex h-full items-center">
			<ProgressBar value={value} color="green" />
			<Text size="paragraph-3" dangerouslySetClassName="pl-2">
				{value}%
			</Text>
		</div>
	);
};

export const TurnStatsCellRenderer = (
	props: CustomCellRendererProps<ReviewDetailsFrontend, ReviewDetailsFrontend["authorTurnStats" | "signerTurnStats"]>
) => {
	// Should be pre-formatted to percent in the value getter
	const value = props.value;

	if (value === null || value === undefined || !("total" in value) || !("completed" in value)) {
		return null;
	}

	const valueAsPercent = Math.min(100, value.total > 0 ? (value.completed / value.total) * 100 : 0);

	return (
		<div className="flex h-full items-center">
			<ProgressBar value={valueAsPercent} color="green" />
			<Text size="paragraph-3" wrap={false} dangerouslySetClassName="pl-2">
				{value.completed}/{value.total}
			</Text>
		</div>
	);
};

export const FormCountCellRenderer = (
	props: CustomCellRendererProps<
		ReviewDetailsFrontend,
		ReviewDetailsFrontend[
			| "unassignedFormsCount"
			| "externalFormsCount"
			| "threeSixtyFormsCount"
			| "pendingThreeSixtyFormsCount"]
	>
) => {
	const value = props.value;

	if (value === null || value === undefined || typeof value !== "number") {
		return null;
	}

	return (
		<div className="flex h-full items-center justify-center">
			<Text size="paragraph-3">{value > 0 ? value : "-"}</Text>
		</div>
	);
};

export const FormCountWithAlertCellRenderer = (
	props: CustomCellRendererProps<
		ReviewDetailsFrontend,
		ReviewDetailsFrontend[
			| "unassignedFormsCount"
			| "externalFormsCount"
			| "threeSixtyFormsCount"
			| "pendingThreeSixtyFormsCount"]
	>
) => {
	const value = props.value;

	if (value === null || value === undefined || typeof value !== "number") {
		return null;
	}

	return (
		<div className="flex h-full items-center justify-center">
			{value > 0 ? (
				<Icon size={12} color="red-5" icon={faCircleExclamation} dangerouslySetClassName="mr-1" />
			) : null}
			<Text size="paragraph-3">{value > 0 ? value : "-"}</Text>
		</div>
	);
};

export const ReviewOptionsCellRenderer = (
	props: CustomCellRendererProps<ReviewDetailsFrontend, ReviewDetailsFrontend["_id"]> & {
		onDeleteClick: (reviewId: string) => void;
	}
) => {
	if (!props?.data?._id) {
		return null;
	}

	const options = [
		{
			key: "deleteReview",
			label: "Delete Review",
			onClick: () => props.onDeleteClick(props.data?._id ?? ""),
		},
	];

	return (
		<div className="flex h-full items-center justify-center">
			<DropDownMenu options={options} placement="bottom-end">
				<Icon icon={faEllipsis} size={16} color="neutral-7" />
			</DropDownMenu>
		</div>
	);
};
